import Box from '@mui/material/Box';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { TextFormField } from '~components/FormFields/TextFormField';
import { Project } from '~hooks/useProjects/models';
import { useStores } from '~store';
import { nullableStringOrNumberIsValidNumber } from '~utils/utilFunctions';

import { FlexColumn, Hint, Label } from './shared-components';

const formatTime = (minutes: number) => {
  const days = Math.floor(minutes / 60 / 24);
  const hours = Math.floor((minutes / 60) % 24);
  const mins = Math.floor(minutes % 60);

  return `${days > 0 ? `${days}d ` : ''}${hours}h ${mins}m`;
};

interface CycleProps {
  forOrderFormV2?: boolean;
  project?: Project;
}

export default function Cycle({ forOrderFormV2, project }: CycleProps) {
  const form = useFormContext();

  const { userStore } = useStores();

  const shouldUseKilometers = userStore.userCompany.isMeters;

  const isEditing = Boolean(form.watch('id'));
  const watchedDropoffOnsite = form.watch('dropoffOnsite') as string | number | null;
  const watchedPickupOnsite = form.watch('pickupOnsite') as string | number | null;
  const watchedPickupToDropoff = form.watch('pickupToDropoff') as string | number | null;

  const formattedDurations = useMemo(() => {
    const isValidPickupOnsite =
      nullableStringOrNumberIsValidNumber(watchedPickupOnsite) &&
      Number(watchedPickupOnsite) >= 0;

    const isValidDropoffOnsite =
      nullableStringOrNumberIsValidNumber(watchedDropoffOnsite) &&
      Number(watchedDropoffOnsite) >= 0;

    const isValidPickupToDropoff =
      nullableStringOrNumberIsValidNumber(watchedPickupToDropoff) &&
      Number(watchedPickupToDropoff) >= 0;

    return {
      pickupOnsite: isValidPickupOnsite ? formatTime(Number(watchedPickupOnsite)) : null,
      dropoffOnsite: isValidDropoffOnsite
        ? formatTime(Number(watchedDropoffOnsite))
        : null,
      pickupToDropoff: isValidPickupToDropoff
        ? formatTime(Number(watchedPickupToDropoff))
        : null,
    };
  }, [watchedPickupOnsite, watchedDropoffOnsite, watchedPickupToDropoff]);

  useEffect(() => {
    if (forOrderFormV2 && !isEditing) {
      const jobTime = isNil(project?.jobTime) ? null : Number(project.jobTime / 60);

      form.setValue('jobTime', jobTime, { shouldDirty: false });
    }
  }, [forOrderFormV2, project?.jobTime, isEditing]);

  return (
    <Box
      sx={{
        display: 'grid',
        gap: forOrderFormV2 ? 2 : 1,
        gridTemplateColumns: forOrderFormV2 ? 'repeat(3, 1fr)' : 'repeat(4, 1fr)',
        mt: forOrderFormV2 ? undefined : 2,
      }}
    >
      <FlexColumn noGap>
        <Label>{t('dispatch.order.cycle.pickup_onsite')}</Label>
        <TextFormField
          control={form.control}
          errors={form.formState.errors}
          name="pickupOnsite"
          type="number"
          dataUi={`page-sales-orders__cycle__pickup-onsite-input`}
        />
        <Hint isLarge>{formattedDurations.pickupOnsite}</Hint>
      </FlexColumn>
      <FlexColumn noGap>
        <Label>{t('dispatch.order.cycle.dropoff_onsite')}</Label>
        <TextFormField
          control={form.control}
          errors={form.formState.errors}
          name="dropoffOnsite"
          type="number"
          dataUi={`page-sales-orders__cycle__dropoff-onsite-input`}
        />
        <Hint isLarge>{formattedDurations.dropoffOnsite}</Hint>
      </FlexColumn>
      <FlexColumn noGap>
        <Label>{t('dispatch.order.cycle.pickup_to_drop')}</Label>
        <TextFormField
          control={form.control}
          errors={form.formState.errors}
          name="pickupToDropoff"
          type="number"
          dataUi={`page-sales-orders__cycle__pickup-to-dropoff-input`}
        />
        <Hint isLarge>{formattedDurations.pickupToDropoff}</Hint>
      </FlexColumn>
      <FlexColumn noGap>
        <Label>
          {t('dispatch.order.cycle.cycle_distance', {
            unit: shouldUseKilometers ? t('common.kms') : t('common.mis'),
          })}
        </Label>
        <TextFormField
          control={form.control}
          errors={form.formState.errors}
          name="cycleDistance"
          type="number"
          dataUi={`page-sales-orders__cycle__cycle-distance-input`}
        />
        <Hint>{t('dispatch.order.cycle.cycle_distance_hint')}</Hint>
      </FlexColumn>
      {forOrderFormV2 ? (
        <>
          <FlexColumn noGap>
            <Label>{t('dispatch.order.cycle.cycle_time')}</Label>
            <TextFormField
              control={form.control}
              errors={form.formState.errors}
              name="cycleTime"
              type="number"
              dataUi={`page-sales-orders__cycle__cycle-time-input`}
            />
          </FlexColumn>

          <FlexColumn noGap>
            <Label>{t('form_fields.truck_hours')}</Label>
            <TextFormField
              control={form.control}
              errors={form.formState.errors}
              name="jobTime"
              type="number"
              dataUi={`page-sales-orders__cycle__job-time-input`}
            />
          </FlexColumn>
        </>
      ) : null}
    </Box>
  );
}
